import Dexie, { Table } from 'dexie';
import {
  ExamSubject,
  IBackendExamJson,
  IExamQuestionPath,
  QuestionCategory,
  UGExamQuestionType,
  ExamStatus,
} from '../types/user-generated-exam';

export interface ICachedGeneratedExam {
  id: string;
  status: ExamStatus;
  examJson: {
    questions: Array<{
      id: string;
      question: string;
      marks: number;
      blooms?: string;
      category: QuestionCategory;
      type: UGExamQuestionType;
      formatType: string;
      mainQuestion?: string;
      answerLines?: number;
      subQuestions?: Array<{ text: string; marks: number; lines: number }>;
      rawQuestion: string;
      image?: string;
      _sourcePath: IExamQuestionPath;
    }>;
    metadata?: Record<string, any>;
  };
  rawExamJson?: IBackendExamJson;
  examHtml: string;
  tags: {
    grade: number;
    subject: ExamSubject;
    pageRanges: string;
    questionTypes: QuestionCategory[];
    seenCategories?: UGExamQuestionType[] | null;
    unseenCategories?: UGExamQuestionType[] | null;
    unseenObjectiveTypes?: string[] | null;
    unseenSubjectiveTypes?: string[] | null;
    imageGenerationEnabled?: boolean;
  };
  metadata: Record<string, any>;
  created: string;
  modified: string;
  cachedAt: number;
  lastAccessed?: number;
  selectedQuestionIds?: string[] | null;
}

/**
 * Strip the pre-rendered `examHtml` field from a cached exam record.
 *
 * `examHtml` is a 50–500 KB string stored on every row alongside `examJson`
 * and `rawExamJson`. It is never read by any UI or DSM component (all
 * rendering paths re-derive HTML from `examJson`). Hydrating it on every
 * list query bloats heap by 5–25 MB on the UG-EG history screen and was
 * implicated in the OOM crash investigation (MC20-18111).
 *
 * Stripping at the read boundary keeps the IndexedDB row untouched.
 *
 * Mirror of `stripCourseThumbnailBase64` in `models/teachertraining.ts`.
 */
export const stripExamHtml = (
  record: ICachedGeneratedExam,
): ICachedGeneratedExam => {
  if (record.examHtml === undefined) return record;
  const { examHtml: _drop, ...rest } = record;
  return rest as ICachedGeneratedExam;
};

export class UserGeneratedExamDB extends Dexie {
  generatedExams!: Table<ICachedGeneratedExam>;

  constructor() {
    super('UserGeneratedExamDB');
    this.version(1).stores({
      generatedExams:
        'id, status, created, modified, cachedAt, lastAccessed, tags.grade, tags.subject',
    });
  }

  async cacheExam(exam: Omit<ICachedGeneratedExam, 'cachedAt'>): Promise<void> {
    const cachedExam: ICachedGeneratedExam = {
      ...exam,
      cachedAt: Date.now(),
      lastAccessed: Date.now(),
    };

    await this.generatedExams.put(cachedExam);
  }

  async getCachedExams(): Promise<ICachedGeneratedExam[]> {
    const records = await this.generatedExams
      .orderBy('created')
      .reverse()
      .toArray();
    return records.map(stripExamHtml);
  }

  async getCachedExamById(
    id: string,
  ): Promise<ICachedGeneratedExam | undefined> {
    const exam = await this.generatedExams.get(id);
    if (exam) {
      await this.generatedExams.update(id, { lastAccessed: Date.now() });
    }
    return exam;
  }

  async getStorageStats(): Promise<{
    total: number;
    ready: number;
    pending: number;
  }> {
    const total = await this.generatedExams.count();
    const ready = await this.generatedExams
      .where('status')
      .equals(ExamStatus.Ready)
      .count();
    const pending = await this.generatedExams
      .where('status')
      .equals(ExamStatus.Pending)
      .count();

    return { total, ready, pending };
  }

  async updateSelectionIds(
    examId: string,
    selectedQuestionIds: string[] | null,
  ): Promise<void> {
    await this.generatedExams.update(examId, { selectedQuestionIds });
  }

  async deleteExam(id: string): Promise<void> {
    await this.generatedExams.delete(id);
  }

  clearData() {
    return this.generatedExams.clear();
  }
}

export const userGeneratedExamDB = new UserGeneratedExamDB();
